// Importaciones de Firebase eliminadas - usando window.auth en su lugar

// Tipos para las configuraciones
export interface EcommercePlatformConfig {
  id: string;
  name: string;
  enabled: boolean;
  description: string;
  icon: string;
  connected: boolean;
  apiKey?: string;
  apiSecret?: string;
  lastUpdated: any;
}

export interface SocialMediaPlatformConfig {
  id: string;
  name: string;
  enabled: boolean;
  description: string;
  icon: string;
  connected: boolean;
  accessToken?: string;
  refreshToken?: string;
  lastUpdated: any;
}

export interface UserPlatformConfigs {
  ecommerce: EcommercePlatformConfig[];
  socialMedia: SocialMediaPlatformConfig[];
  lastUpdated: any;
}

class PlatformConfigService {
  private db: any;
  private auth: any;

  constructor() {
    // No inicializar aquí, se hará dinámicamente
  }

  private ensureFirebaseInitialized() {
    if (!this.db || !this.auth) {
      // Obtener instancias de Firebase desde el objeto global
      if (typeof window !== 'undefined' && (window as any).auth) {
        this.db = (window as any).auth.db;
        this.auth = (window as any).auth.auth;
        console.log('🔧 Firebase inicializado dinámicamente:', {
          hasDb: !!this.db,
          hasAuth: !!this.auth
        });
      } else {
        console.error('❌ Firebase no está disponible en window.auth');
        throw new Error('Firebase no está inicializado');
      }
    }
  }

  private getUserId(): string | null {
    // Intentar obtener el usuario desde window.auth.auth.currentUser
    if (typeof window !== 'undefined' && (window as any).auth?.auth?.currentUser) {
      const user = (window as any).auth.auth.currentUser;
      console.log('🔍 Usuario encontrado en window.auth.auth.currentUser:', user?.uid);
      return user?.uid || null;
    }
    
    // Fallback al método anterior
    const user = this.auth?.currentUser;
    console.log('🔍 Usuario encontrado en this.auth.currentUser:', user?.uid);
    return user?.uid || null;
  }

  private getUserConfigsDoc(userId: string) {
    this.ensureFirebaseInitialized();
    // Usar window.auth.doc en lugar de doc(this.db, ...)
    return (window as any).auth.doc((window as any).auth.db, 'user_configs', userId);
  }

  // Obtener todas las configuraciones del usuario
  async getUserConfigs(): Promise<UserPlatformConfigs | null> {
    try {
      const userId = this.getUserId();
      if (!userId) {
        console.warn('No hay usuario autenticado');
        return null;
      }

      const configsDoc = this.getUserConfigsDoc(userId);
      const configsSnapshot = await (window as any).auth.getDoc(configsDoc);

      if (configsSnapshot.exists()) {
        const data = configsSnapshot.data();
        console.log('✅ Configuraciones cargadas desde Firebase:', data);
        return data as UserPlatformConfigs;
      } else {
        console.log('📝 No hay configuraciones guardadas, usando valores por defecto');
        return null;
      }
    } catch (error) {
      console.error('❌ Error cargando configuraciones desde Firebase:', error);
      throw error;
    }
  }

  // Guardar todas las configuraciones del usuario
  async saveUserConfigs(configs: Partial<UserPlatformConfigs>): Promise<void> {
    try {
      const userId = this.getUserId();
      if (!userId) {
        throw new Error('No hay usuario autenticado');
      }

      const configsDoc = this.getUserConfigsDoc(userId);
      
      // Preparar datos con timestamp
      const dataToSave = {
        ...configs,
        lastUpdated: (window as any).auth.serverTimestamp()
      };

      await (window as any).auth.setDoc(configsDoc, dataToSave, { merge: true });
      console.log('✅ Configuraciones guardadas en Firebase:', dataToSave);
    } catch (error) {
      console.error('❌ Error guardando configuraciones en Firebase:', error);
      throw error;
    }
  }

  // Actualizar configuraciones de e-commerce
  async updateEcommerceConfigs(ecommerceConfigs: EcommercePlatformConfig[]): Promise<void> {
    try {
      const userId = this.getUserId();
      if (!userId) {
        throw new Error('No hay usuario autenticado');
      }

      const configsDoc = this.getUserConfigsDoc(userId);
      
      // Filtrar valores undefined antes de guardar
      const cleanEcommerceConfigs = ecommerceConfigs.map(config => {
        const cleanConfig: any = {};
        Object.keys(config).forEach(key => {
          if (config[key as keyof typeof config] !== undefined) {
            cleanConfig[key] = config[key as keyof typeof config];
          }
        });
        return cleanConfig;
      });

      await (window as any).auth.setDoc(configsDoc, {
        ecommerce: cleanEcommerceConfigs,
        lastUpdated: (window as any).auth.serverTimestamp()
      }, { merge: true });

      console.log('✅ Configuraciones de e-commerce actualizadas en Firebase');
    } catch (error) {
      console.error('❌ Error actualizando configuraciones de e-commerce:', error);
      throw error;
    }
  }

  // Actualizar configuraciones de redes sociales
  async updateSocialMediaConfigs(socialConfigs: SocialMediaPlatformConfig[]): Promise<void> {
    try {
      const userId = this.getUserId();
      if (!userId) {
        throw new Error('No hay usuario autenticado');
      }

      const configsDoc = this.getUserConfigsDoc(userId);
      
      // Filtrar valores undefined antes de guardar
      const cleanSocialConfigs = socialConfigs.map(config => {
        const cleanConfig: any = {};
        Object.keys(config).forEach(key => {
          if (config[key as keyof typeof config] !== undefined) {
            cleanConfig[key] = config[key as keyof typeof config];
          }
        });
        return cleanConfig;
      });

      await (window as any).auth.setDoc(configsDoc, {
        socialMedia: cleanSocialConfigs,
        lastUpdated: (window as any).auth.serverTimestamp()
      }, { merge: true });

      console.log('✅ Configuraciones de redes sociales actualizadas en Firebase');
    } catch (error) {
      console.error('❌ Error actualizando configuraciones de redes sociales:', error);
      throw error;
    }
  }

  // Obtener configuraciones de e-commerce
  async getEcommerceConfigs(): Promise<EcommercePlatformConfig[]> {
    try {
      const userConfigs = await this.getUserConfigs();
      return userConfigs?.ecommerce || [];
    } catch (error) {
      console.error('❌ Error obteniendo configuraciones de e-commerce:', error);
      return [];
    }
  }

  // Obtener configuraciones de redes sociales
  async getSocialMediaConfigs(): Promise<SocialMediaPlatformConfig[]> {
    try {
      const userConfigs = await this.getUserConfigs();
      return userConfigs?.socialMedia || [];
    } catch (error) {
      console.error('❌ Error obteniendo configuraciones de redes sociales:', error);
      return [];
    }
  }

  // Limpiar configuraciones (para logout)
  async clearUserConfigs(): Promise<void> {
    try {
      const userId = this.getUserId();
      if (!userId) {
        return; // No hay usuario autenticado, no hay nada que limpiar
      }

      // No eliminamos el documento, solo lo marcamos como vacío
      const configsDoc = this.getUserConfigsDoc(userId);
      await (window as any).auth.setDoc(configsDoc, {
        ecommerce: [],
        socialMedia: [],
        lastUpdated: (window as any).auth.serverTimestamp()
      });

      console.log('✅ Configuraciones limpiadas');
    } catch (error) {
      console.error('❌ Error limpiando configuraciones:', error);
    }
  }
}

// Crear instancia singleton
const platformConfigService = new PlatformConfigService();

export default platformConfigService;
